/**
 * OSS 服务端中转上传：为 el-upload / FormData 附加 SchId，图片 URL 展示兼容本机与 OSS。
 * 创建于 2026-06-29
 */
import axios from 'axios'
import { BaseApiUrl } from '../api/base'

/**
 * 剥掉 OSS 预签名参数（Expires / OSSAccessKeyId / Signature / x-oss-*）。
 * 非 OSS HTTPS 地址原样返回，防止数据库存入临时签名 URL。
 * 最后更新：2026-06-30
 */
export function stripOssPresignParams(url) {
  if (!url || typeof url !== 'string') return url
  if (!/^https?:\/\//i.test(url)) return url
  try {
    const u = new URL(url)
    if (u.host.indexOf('.oss-') >= 0 && u.host.indexOf('.aliyuncs.com') >= 0) {
      return u.origin + u.pathname
    }
  } catch (_) {}
  return url
}

/**
 * 本机相对路径拼 API 前缀；已是 https 的 OSS 地址原样返回。
 */
export function resolveImageSrc(path, baseApiUrl = BaseApiUrl) {
  if (!path) return ''
  if (/^(https?:|blob:|data:)/i.test(path)) return path
  return (baseApiUrl || BaseApiUrl) + path
}

/**
 * 从页面实例解析学校 ID：优先顶部「切换学校」统一 ID，避免左侧树选中班级/年级时传错。
 * 最后更新：2026-06-29
 */
export function getUploadSchoolId(vm) {
  // 1. 全局切换学校（App.vue 顶部选择器）——管理端统一上下文
  const storeId = vm && vm.$store && vm.$store.state && vm.$store.state.activeSchoolId
  if (storeId != null && storeId !== '' && storeId !== 0 && storeId !== '0') {
    return String(storeId)
  }
  if (typeof window !== 'undefined') {
    const cached = window.localStorage.getItem('activeSchoolId')
    if (cached && cached !== '0') return String(cached)
  }

  // 2. 页面内显式学校字段（班牌等独立页）
  if (!vm) return ''
  const pageCandidates = [
    vm.SchoolId,
    vm.currentSchId,
    vm.schoolSchId,
    vm.schoolId
  ]
  for (let i = 0; i < pageCandidates.length; i++) {
    const id = pageCandidates[i]
    if (id != null && id !== '' && id !== 0 && id !== '0') {
      return String(id)
    }
  }
  return ''
}

/**
 * 合并 SchId 与额外字段（Foo 等），供 el-upload :data 使用。
 */
export function buildOssUploadData(vm, extra) {
  const data = Object.assign({}, extra || {})
  const schId = getUploadSchoolId(vm)
  if (schId) data.SchId = schId
  return data
}

/**
 * 本机图片上传 data（不传 SchId，ImgController 走 WebRoot 相对路径）。
 * 用于富文本插图等需班牌/多端直链回显的场景。创建于 2026-09-22
 */
export function buildLocalPicUploadData(foo) {
  const data = {}
  if (foo) data.Foo = foo
  return data
}

/**
 * 本站静态资源路径前缀（历史富文本常写死 https://web.yiqiba.xin/...）。创建于 2026-09-22
 */
const LOCAL_STATIC_PATH_PREFIXES = [
  '/images/',
  '/appmenupic/',
  '/static/',
  '/organizationChart/',
  '/NoticeRecord/',
  '/DeptNoticeRecord/',
  '/ScreenSaver/',
  '/ClassHomework/',
  '/DeptStyleRecord/',
  '/DeptVideoRecord/',
  '/DeptExtensionInfo/',
  '/HonorList/',
]

/**
 * 是否为本站静态图相对路径（含历史未带前导 / 的写法）。创建于 2026-09-22
 */
function isLocalStaticRelativePath(pathname) {
  if (!pathname || typeof pathname !== 'string') return false
  let p = pathname.trim()
  if (!p) return false
  if (p.charAt(0) !== '/') p = '/' + p
  return LOCAL_STATIC_PATH_PREFIXES.some((prefix) => p.indexOf(prefix) === 0)
}

/**
 * 将本站 API / 历史静态域名绝对图址收成相对路径；OSS 地址仅剥预签名。
 * 兼容本地联调 BaseApiUrl=localhost 时库内仍为 https://web.yiqiba.xin/images/... 的情况。
 * 创建于 2026-09-22，最后修改：2026-09-22
 */
export function normalizeImageUrlForSave(url, baseApiUrl = BaseApiUrl) {
  if (!url || typeof url !== 'string') return url
  let u = String(url).trim()
  if (!u) return u
  u = stripOssPresignParams(u)
  const base = String(baseApiUrl || BaseApiUrl || '').replace(/\/$/, '')
  if (base && u.indexOf(base + '/') === 0) {
    return u.slice(base.length)
  }
  if (base && u === base) {
    return '/'
  }
  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u)
      // 私有 OSS 保留完整 https（班牌也无法直链，另行处理）
      if (parsed.host.indexOf('.oss-') >= 0 && parsed.host.indexOf('.aliyuncs.com') >= 0) {
        return parsed.origin + parsed.pathname
      }
      const pathname = parsed.pathname || ''
      if (isLocalStaticRelativePath(pathname)) {
        // web.yiqiba.xin / api.yiqiba.xin 等本站静态域名 → 相对路径
        return pathname
      }
      // BaseApiUrl 与历史域名仅 host 不同时，同路径仍收成相对路径
      if (base) {
        try {
          const baseHost = new URL(base).host
          if (baseHost && parsed.host === baseHost && pathname) {
            return pathname
          }
        } catch (_) {}
      }
    } catch (_) {}
  }
  return u
}

/**
 * 富文本 HTML 入库前规范化：img/src 剥 OSS 签名，本站绝对地址还原为相对路径。
 * 创建于 2026-09-22
 */
export function normalizeRichTextHtmlForSave(html, baseApiUrl = BaseApiUrl) {
  if (!html || typeof html !== 'string') return html || ''
  if (typeof document === 'undefined') {
    return html.replace(/src\s*=\s*(["'])([^"']*)\1/gi, (match, quote, src) => {
      const normalized = normalizeImageUrlForSave(src, baseApiUrl)
      return `src=${quote}${normalized}${quote}`
    })
  }
  const container = document.createElement('div')
  container.innerHTML = html
  const imgs = container.querySelectorAll('img[src]')
  imgs.forEach((img) => {
    const src = img.getAttribute('src')
    if (!src) return
    img.setAttribute('src', normalizeImageUrlForSave(src, baseApiUrl))
  })
  return container.innerHTML
}

/**
 * 富文本 HTML 编辑回显：本机相对路径拼 API 前缀，便于 Quill 直链显示。创建于 2026-09-22
 */
export function prepareRichTextHtmlForDisplay(html, baseApiUrl = BaseApiUrl) {
  if (!html || typeof html !== 'string') return html || ''
  const base = baseApiUrl || BaseApiUrl
  if (typeof document === 'undefined') {
    return html.replace(/src\s*=\s*(["'])([^"']*)\1/gi, (match, quote, src) => {
      const display = resolveImageSrc(stripOssPresignParams(src), base)
      return `src=${quote}${display}${quote}`
    })
  }
  const container = document.createElement('div')
  container.innerHTML = html
  const imgs = container.querySelectorAll('img[src]')
  imgs.forEach((img) => {
    const src = img.getAttribute('src')
    if (!src) return
    img.setAttribute('src', resolveImageSrc(stripOssPresignParams(src), base))
  })
  return container.innerHTML
}

/**
 * 向 FormData 追加 SchId（axios 手动上传场景）。
 */
export function appendSchIdToFormData(formData, vm) {
  const schId = getUploadSchoolId(vm)
  if (schId) formData.append('SchId', schId)
  return formData
}

/**
 * 从头像所属部门 Dids 生成 Foo 子目录（年级/班级或部门 ID）。
 */
export function buildHeadPicFooFromDids(dids) {
  const list = dids || []
  if (list.length >= 2) {
    return list.slice(-2).map(String).join('/')
  }
  if (list.length === 1) {
    return String(list[0])
  }
  return ''
}

/** 选择头像后仅本地预览，保存时再上传。最后更新：2026-06-29 */
export function attachLocalAvatarFile(file) {
  return {
    pendingAvatarFile: file,
    pendingAvatarPreviewUrl: URL.createObjectURL(file)
  }
}

/** 释放 blob 预览 URL。 */
export function revokeLocalAvatarPreview(url) {
  if (url && String(url).indexOf('blob:') === 0) {
    URL.revokeObjectURL(url)
  }
}

/**
 * 提交表单时上传头像到 HeadPic 接口（含 SchId / Foo / Category）。
 * @param {File} file
 * @param {Vue} vm
 * @param {string} [foo] - 业务子目录（如部门 Id）
 * @param {string} [category] - 'AvatarStudent'（默认）或 'AvatarTeacher'
 */
/**
 * 从上传响应或 axios 错误中解析业务提示文案。
 */
function resolveUploadErrorMessage(resOrErr, fallback) {
  if (!resOrErr) return fallback
  // 拦截器在部分 HTTP 错误时 resolve 为 { success:false, message }
  if (resOrErr.success === false && (resOrErr.message || resOrErr.Message) && resOrErr.data == null && resOrErr.status == null) {
    return resOrErr.message || resOrErr.Message || fallback
  }
  const body = resOrErr.data || (resOrErr.response && resOrErr.response.data)
  const msg = body && (body.msg || body.Msg || body.message || body.Message)
  if (msg) return msg
  if (resOrErr.message && resOrErr.message !== 'Network Error') return resOrErr.message
  return fallback
}

export async function uploadHeadPic(file, vm, foo, category) {
  if (!file) {
    throw new Error('未选择头像文件')
  }
  const formData = new FormData()
  formData.append('Files', file)
  const extra = {}
  if (foo) extra.Foo = foo
  // category 须为字符串；误传 options 对象时忽略，避免 FormData 写入 [object Object]
  if (category && typeof category === 'string') extra.Category = category
  const data = buildOssUploadData(vm, extra)
  Object.keys(data).forEach((key) => {
    formData.append(key, data[key])
  })
  const baseApiUrl = (vm && vm.BaseApiUrl) || BaseApiUrl
  let res
  try {
    res = await axios.post(`${baseApiUrl}/images/Upload/HeadPic`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      // 由调用方统一 toast，避免与提交 catch 重复提示
      skipGlobalHttpErrorToast: true
    })
  } catch (e) {
    throw new Error(resolveUploadErrorMessage(e, '头像上传失败'))
  }
  // 未 skip 时拦截器可能 resolve 为 { success:false, message } 而非 AxiosResponse
  if (!res || (res.success === false && res.data == null && res.status == null)) {
    throw new Error(resolveUploadErrorMessage(res, '头像上传失败'))
  }
  const body = res.data
  const success = body && (body.success === true || body.Success === true)
  if (!success) {
    throw new Error(resolveUploadErrorMessage(res, '头像上传失败'))
  }
  const url = stripOssPresignParams(body.response != null ? body.response : body.Response)
  return { url, sizeKb: body.msgDev }
}

/**
 * 提交表单时上传普通图片到 Pic 接口（含 SchId / Foo）。支持通过 options.noOss 跳过 OSS。最后更新：2026-09-06
 */
export async function uploadPic(file, vm, foo, options) {
  if (!file) {
    throw new Error('未选择图片文件')
  }
  const formData = new FormData()
  formData.append('Files', file)
  const extra = foo ? { Foo: foo } : {}
  const skipOss = options && (options.noOss || options.skipOss)
  const data = skipOss ? extra : buildOssUploadData(vm, extra)
  Object.keys(data).forEach((key) => {
    formData.append(key, data[key])
  })
  const baseApiUrl = (vm && vm.BaseApiUrl) || BaseApiUrl
  let res
  try {
    res = await axios.post(`${baseApiUrl}/images/Upload/Pic`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      skipGlobalHttpErrorToast: true
    })
  } catch (e) {
    throw new Error(resolveUploadErrorMessage(e, '图片上传失败'))
  }
  if (!res || (res.success === false && res.data == null && res.status == null)) {
    throw new Error(resolveUploadErrorMessage(res, '图片上传失败'))
  }
  const body = res.data
  const success = body && (body.success === true || body.Success === true)
  if (!success) {
    throw new Error(resolveUploadErrorMessage(res, '图片上传失败'))
  }
  let url = stripOssPresignParams(body.response != null ? body.response : body.Response)
  if (Array.isArray(url)) url = url[0]
  if (typeof url === 'string' && url.indexOf(';') >= 0) {
    url = stripOssPresignParams(url.split(';')[0])
  }
  return { url, sizeKb: body.msgDev }
}
